// Sums the `value` (deal amount) of a list of funnel cards, tolerating missing
// or string-serialized values.
export const sumFunnelValues = (cards = []) =>
  cards.reduce((total, card) => total + (Number(card.value) || 0), 0);

// Formats a deal amount as a discreet, whole-number currency string
// (e.g. "R$ 12.000"). Returns an empty string for absent/zero values so
// callers can hide the label entirely.
export const formatFunnelValue = value => {
  const amount = Number(value);
  if (!amount) return '';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(amount);
};

// True when a custom field value is worth showing (mirrors the backend's
// "empty" definition: absent, null, blank string or empty array/object).
export const hasFieldValue = value => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

// True when the board's field filter has a usable criterion for its type, so
// the board should actually apply it (and flag the filter as active). The
// criterion shape depends on the field type:
//   text:   { query }        substring, case-insensitive
//   number: { min, max }      inclusive range, either bound optional
//   date:   { from, to }      inclusive range, either bound optional
//   list:   { option }        equals the selected option
export const isFieldCriterionActive = (fieldType, criterion = {}) => {
  const filled = value => value !== '' && value !== null && value !== undefined;

  switch (fieldType) {
    case 'number':
      return filled(criterion.min) || filled(criterion.max);
    case 'date':
      return filled(criterion.from) || filled(criterion.to);
    case 'list':
      return filled(criterion.option);
    case 'text':
    default:
      return Boolean((criterion.query || '').trim());
  }
};

// True when a card's raw custom field value matches the active field filter,
// per the field type. An empty/inactive criterion matches everything (so the
// filter is a no-op until the user types a value). A card missing the value
// never matches an active number/date range or list criterion.
export const matchesFieldFilter = (fieldType, criterion, value) => {
  if (!isFieldCriterionActive(fieldType, criterion)) return true;

  switch (fieldType) {
    case 'number': {
      const num = Number(value);
      if (!hasFieldValue(value) || Number.isNaN(num)) return false;
      const { min, max } = criterion;
      if (min !== '' && min != null && num < Number(min)) return false;
      if (max !== '' && max != null && num > Number(max)) return false;
      return true;
    }
    case 'date': {
      // Values are ISO 'yyyy-mm-dd' strings, so string comparison orders them
      // chronologically without parsing.
      if (!hasFieldValue(value)) return false;
      const { from, to } = criterion;
      if (from && value < from) return false;
      if (to && value > to) return false;
      return true;
    }
    case 'list':
      return value === criterion.option;
    case 'text':
    default:
      return String(value ?? '')
        .toLowerCase()
        .includes(criterion.query.trim().toLowerCase());
  }
};

// Renders a custom field value for display on the card, per its field type:
// text as-is, number grouped in pt-BR, date as dd/mm/yyyy, list as the option
// label. Returns '' for empty values so callers can hide the field.
export const formatFieldValue = (field, value) => {
  if (!hasFieldValue(value)) return '';

  switch (field?.field_type) {
    case 'number': {
      const amount = Number(value);
      return Number.isNaN(amount)
        ? String(value)
        : new Intl.NumberFormat('pt-BR').format(amount);
    }
    case 'date': {
      const date = new Date(`${value}T00:00:00`);
      return Number.isNaN(date.getTime())
        ? String(value)
        : new Intl.DateTimeFormat('pt-BR').format(date);
    }
    default:
      return String(value);
  }
};
